import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { choice_array, formArrayItem, matrixRow } from '../preview-forms.interface';

/**
 * แสดงคำถามแบบ Matrix/Grid (หลายแถว x scale เดียวกัน) — ต่อยอดจาก likert
 * อ่าน matrix_rows (แถวจริงจาก BE พร้อม matrix_row_id) เป็นรายชื่อแถว โดยตัวเลือก (choices) เป็นคอลัมน์
 * เมื่อเลือก emit ค่ากลับให้ parent เป็น choice_array[] (question_choice_id + matrix_row_id ต่อแถว)
 */
@Component({
    selector: 'app-form-matrix',
    templateUrl: './form-matrix.component.html',
    styleUrls: ['./form-matrix.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormMatrixComponent {
  form!: formArrayItem;
  /** matrix_row_id -> question_choice_id ที่เลือก (โหมด radio เลือกคอลัมน์เดียว) */
  rowValues: { [rowId: string]: string } = {};
  /** matrix_row_id -> question_choice_id[] ที่เลือก (โหมด checkbox หลายคอลัมน์) */
  rowValuesMulti: { [rowId: string]: string[] } = {};

  @Output() update = new EventEmitter<choice_array[]>();
  @Output() valid = new EventEmitter<boolean>();

  /** true = โหมดเลือกได้หลายคอลัมน์ต่อแถว (checkbox) */
  get isMulti(): boolean { return !!this.form?.matrix_multiple; }

  @Input('model') set item(v: formArrayItem) {
    this.form = v;
    // เรียกค่าเดิมกลับมาแสดงตอนกลับหน้า (จาก choices_value ที่ parent เก็บไว้)
    this.rowValues = {};
    this.rowValuesMulti = {};
    // เตรียม array ว่างให้ทุกแถว เพื่อให้ ngModel ของ checkbox ผูกกับ array จริง
    this.rows.forEach((row) => {
      this.rowValuesMulti[row.matrix_row_id] = this.rowValuesMulti[row.matrix_row_id] || [];
    });
    (v?.choices_value || []).forEach((c: choice_array) => {
      if (c.matrix_row_id) {
        this.rowValues[c.matrix_row_id] = c.question_choice_id;
        (this.rowValuesMulti[c.matrix_row_id] = this.rowValuesMulti[c.matrix_row_id] || [])
          .push(c.question_choice_id);
      }
    });
    this.emitValid();
  }

  get rows(): matrixRow[] {
    const raw: any[] = this.form?.matrix_rows || [];
    // รองรับทั้งรูปแบบใหม่ (matrixRow มี id) และเก่า (string label ล้วน จาก preview เจ้าของ)
    return raw.map((r, i) =>
      typeof r === 'string'
        ? { matrix_row_id: 'row-' + i, row_label: r, sequence_no: i }
        : r
    );
  }

  trackByIndex(index: number): number { return index; }

  selectCell(rowId: string, choiceId: string) {
    if (this.form?.view) return;
    this.rowValues[rowId] = choiceId;
    this.emit();
  }

  /** โหมด checkbox: p-checkbox จัดการ membership ของ array เอง แค่ emit ต่อ */
  onMultiChange() {
    if (this.form?.view) return;
    this.emit();
  }

  private emit() {
    let choices: choice_array[];
    if (this.isMulti) {
      choices = [];
      this.rows.forEach((row) => {
        const rowId = row.matrix_row_id;
        (this.rowValuesMulti[rowId] || []).forEach((choiceId) => {
          choices.push({ question_choice_id: choiceId, matrix_row_id: rowId });
        });
      });
    } else {
      choices = Object.keys(this.rowValues)
        .filter((rowId) => !!this.rowValues[rowId])
        .map((rowId) => ({ question_choice_id: this.rowValues[rowId], matrix_row_id: rowId }));
    }
    this.update.emit(choices);
    this.emitValid();
  }

  /** required + ยังตอบไม่ครบทุกแถว => valid=true (แปลว่า "ยังไม่ผ่าน" ตาม convention ของ preview) */
  private emitValid() {
    if (!this.form?.question_required) { this.valid.emit(false); return; }
    const answered = this.isMulti
      ? this.rows.filter((r) => (this.rowValuesMulti[r.matrix_row_id]?.length || 0) > 0).length
      : this.rows.filter((r) => !!this.rowValues[r.matrix_row_id]).length;
    this.valid.emit(answered < this.rows.length);
  }
}
